import { styled } from '@mui/material';
import APIRoleBasedRouting from 'app/APIRoleBasedRouting';
import ProviderWrapper from 'app/ProviderWrapper';
import axios from 'axios';
import Header from 'components/Header';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Loading, Stack } from 'shell/components';

axios.defaults.baseURL = process.env.REACT_APP_API_ENDPOINT;
axios.interceptors.response.use(
    ({ data }) => data,
    ({ response = {} }) => {
        if (response.status === 401) {
            localStorage.removeItem('auth_token');
            axios.defaults.headers.common.Authorization = undefined;
            window.location.href = '/';
        }
        return Promise.reject(response);
    }
);

const noHeaderRoutes = ['/components', '/course-set-up', '/lti-incomplete', '/profile-setup'];

const AppHeader = () => {
    const { auth } = useAuth();
    const location = useLocation();
    const { device } = useViewport();

    const isNoHeader = noHeaderRoutes.some((route) => {
        return location.pathname.endsWith(route);
    });

    if (!auth || isNoHeader) return null;

    return (
        <HeaderWrapper device={device}>
            <Header />
        </HeaderWrapper>
    );
};

const App = () => {
    const { loading } = useAuth();

    const renderRoute = () => {
        return <APIRoleBasedRouting />;
    };

    useEffect(() => {
        let observer;

        const updateTitle = () => {
            const h1Elements = document.querySelectorAll('h1');
            if (h1Elements.length === 1) {
                document.title = `${h1Elements[0].textContent} - TimelyGrader`;
            } else if (h1Elements.length > 1) {
                const titles = Array.from(h1Elements)
                    .slice(1)
                    .map((h1) => h1.textContent);
                document.title = `${titles.join(' & ')} - TimelyGrader`;
            }
        };

        observer = new MutationObserver(() => {
            updateTitle();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        updateTitle();

        return () => {
            if (observer) observer.disconnect();
        };
    }, []);

    return loading ? (
        <Loading />
    ) : (
        <ProviderWrapper>
            <PageWrapper>
                <AppHeader />
                <BodyWrapper tabIndex={-1}>{renderRoute()}</BodyWrapper>
            </PageWrapper>
        </ProviderWrapper>
    );
};

const PageWrapper = styled(Stack)(() => {
    return {
        height: '100%',
    };
});

const HeaderWrapper = styled(Box)(({ theme, device }) => {
    return {
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        background: theme.palette.ensightful.shades[0],
        ...(device !== 'mobile' && {
            zIndex: theme.zIndex.drawer + 1,
        }),
    };
});

const BodyWrapper = styled(Box)(({ theme }) => {
    return {
        flex: 1,
        overflow: 'auto',
        background: theme.palette.ensightful.neutral[50],
    };
});

export default App;
